import _get from 'lodash/get';
import { Attachment } from 'graphql-common';
import { Values } from '@lib/interfaces/form';

/**
 * Transforms an attachment from API format to form format for duplication.
 *
 * When duplicating an entity (widget template -> widget), the backend copies
 * the file server-side from the source attachment id rather than the
 * frontend re-sending the whole stored file description.
 *
 * API format: { id, urls: { small, medium, original }, fileData: { url, data: {...} }, filename }
 * Form format: { sourceAttachmentId, url }
 *
 * The `sourceAttachmentId` property tells the backend which attachment's file to
 * copy. The `url` property is kept for local preview, purely so the image is
 * visible immediately, and is stripped before the value reaches the mutation.
 *
 * @param attachment - Attachment in API format from GraphQL query
 * @returns Attachment in form format suitable for mutation, or null if invalid
 */
export default function transformAttachmentForDuplication(
  attachment: Attachment | Values | null | undefined,
): Values | null {
  if (!attachment) return null;

  const id = _get(attachment, 'id') as string | undefined;
  if (!id) return null;

  const previewUrl =
    (_get(attachment, 'fileData.url') as string | undefined) ||
    (_get(attachment, 'urls.small') as string | undefined) ||
    (_get(attachment, 'urls.medium') as string | undefined) ||
    (_get(attachment, 'urls.original') as string | undefined);

  return {
    sourceAttachmentId: id,
    url: previewUrl || undefined,
  };
}

/**
 * Transforms an array of attachments from API format to form format for duplication.
 *
 * @param attachments - Array of attachments in API format
 * @returns Array of attachments in form format (excludes any that couldn't be transformed)
 */
export function transformAttachmentsForDuplication(
  attachments: (Attachment | Values)[] | null | undefined,
): Values[] {
  if (!attachments || attachments.length === 0) return [];

  return attachments
    .map(transformAttachmentForDuplication)
    .filter((att): att is Values => att !== null);
}
